'use client';

import React from 'react';
import ServiceCard from './ServiceCard';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';


interface Service {
  id: number;
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  icon: string;
  image: string;
  alt: string;
  href: string;
}

const ServicesSection = () => {
  const { language, t } = useLanguage();
  
  const services: Service[] = [
  {
    id: 1,
    title: { en: 'Residential Moving', ar: 'نقل سكني' },
    description: { en: 'Complete home relocation services with careful handling of your belongings. From apartments to villas, we ensure a smooth transition to your new home.', ar: 'خدمات نقل منزلية كاملة مع التعامل الدقيق مع ممتلكاتك. من الشقق إلى الفلل، نضمن انتقالاً سلساً إلى منزلك الجديد.' },
    icon: "HomeIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b8bd653-1764700673482.png",
    alt: "Modern living room with packed moving boxes and furniture covered in protective blankets",
    href: "/services"
  },
  {
    id: 2,
    title: { en: 'Commercial Relocation', ar: 'نقل تجاري' },
    description: { en: 'Minimize downtime with our efficient office moving services. We handle equipment, furniture, and sensitive documents with professional care.', ar: 'قلل وقت التوقف مع خدمات نقل المكاتب الفعالة. نتعامل مع المعدات والأثاث والوثائق الحساسة بعناية احترافية.' },
    icon: "BuildingOfficeIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e308d15c-1768148776427.png",
    alt: "Professional office space with desks and computers being prepared for relocation by moving team",
    href: "/services"
  },
  {
    id: 3,
    title: { en: 'Packing Services', ar: 'خدمات التعبئة' },
    description: { en: 'Expert packing with premium materials to protect your valuables. Our team uses industry-standard techniques for maximum safety.', ar: 'تعبئة احترافية بمواد عالية الجودة لحماية ممتلكاتك الثمينة. يستخدم فريقنا تقنيات معيارية للصناعة لأقصى درجات الأمان.' },
    icon: "ArchiveBoxIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16885cae6-1767260679136.png",
    alt: "Professional mover carefully wrapping delicate items in bubble wrap and packing materials",
    href: "/services"
  },
  {
    id: 4,
    title: { en: 'Assembly & Installation', ar: 'التركيب والتثبيت' },
    description: { en: 'Furniture assembly and installation services for your new space. We ensure everything is set up perfectly and ready to use.', ar: 'خدمات تركيب وتثبيت الأثاث لمساحتك الجديدة. نضمن إعداد كل شيء بشكل مثالي وجاهز للاستخدام.' },
    icon: "WrenchScrewdriverIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1849a2e09-1767672715477.png",
    alt: "Skilled technician assembling modern furniture with professional tools in bright room",
    href: "/services"
  }];


  return (
    <section className="py-16 lg:py-24 bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-full mb-4">
            <Icon name="SparklesIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-body font-semibold text-primary">{t({ en: 'Our Services', ar: 'خدماتنا' })}</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
            {t({ en: 'Comprehensive Moving Solutions', ar: 'حلول نقل شاملة' })}
          </h2>
          <p className="text-lg text-text-secondary font-body">
            {t({ en: 'From residential moves to commercial relocations, we provide complete moving services tailored to your needs in Kuwait.', ar: 'من النقل السكني إلى النقل التجاري، نقدم خدمات نقل كاملة مصممة خصيصاً لاحتياجاتك في الكويت.' })}
          </p>
        </div>
        
        {/* Service Information Section */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 lg:p-8 mb-12 border-2 border-primary/10">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="ArchiveBoxIcon" size={24} variant="solid" className="text-white" />
              </div>
              <div>
                <h3 className="font-headline font-bold text-lg text-text-primary mb-2">
                  {t({ en: 'We Pack Anything', ar: 'نعبئ أي شيء' })}
                </h3>
                <p className="text-sm text-text-secondary font-body">
                  {t({ en: 'From delicate items to heavy furniture, we handle all your packing needs with professional care.', ar: 'من العناصر الدقيقة إلى الأثاث الثقيل، نتعامل مع جميع احتياجات التعبئة الخاصة بك بعناية احترافية.' })}
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                <Icon name="CubeIcon" size={24} variant="solid" className="text-white" />
              </div>
              <div>
                <h3 className="font-headline font-bold text-lg text-text-primary mb-2">
                  {t({ en: 'We Bring Our Own Materials', ar: 'نحضر موادنا الخاصة' })}
                </h3>
                <p className="text-sm text-text-secondary font-body">
                  {t({ en: 'Quality packing materials included - boxes, bubble wrap, and protective covers provided by us.', ar: 'مواد تعبئة عالية الجودة مشمولة - صناديق، غلاف فقاعي، وأغطية واقية نوفرها نحن.' })}
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-ocean rounded-lg flex items-center justify-center">
                <Icon name="TruckIcon" size={24} variant="solid" className="text-white" />
              </div>
              <div>
                <h3 className="font-headline font-bold text-lg text-text-primary mb-2">
                  {t({ en: 'We Move Anything From Anyplace', ar: 'ننقل أي شيء من أي مكان' })}
                </h3>
                <p className="text-sm text-text-secondary font-body">
                  {t({ en: 'Anywhere in Kuwait - from Farwaniya to any location, we\'ve got you covered.', ar: 'في أي مكان في الكويت - من الفروانية إلى أي موقع، نحن نغطيك.' })}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) =>
          <ServiceCard
            key={service.id}
            id={service.id}
            title={service.title[language]}
            description={service.description[language]}
            icon={service.icon}
            image={service.image}
            alt={service.alt}
            href={service.href} />

          )}
        </div>
      </div>
    </section>);

};

export default ServicesSection;